import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Store, LoaderCircle, RefreshCw, ArrowLeft, Plus, Pencil, Trash2, ImagePlus } from "lucide-react";
import { Logo } from "../components/landing/Logo";
import { useAuth, formatApiError } from "../contexts/AuthContext";
import { useMasters, PillSelect } from "../components/form/engine";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const NEXT = {
  placed: { to: "packed", label: "Mark Packed" },
  packed: { to: "shipped", label: "Mark Shipped" },
  shipped: { to: "delivered", label: "Mark Delivered" },
};

const STATUS_STYLE = {
  placed: "bg-[#22C55E]/10 text-[#22C55E]",
  packed: "bg-[#F97316]/10 text-[#F97316]",
  shipped: "bg-[#7C3AED]/15 text-[#A78BFA]",
  delivered: "bg-[#06B6D4]/10 text-[#06B6D4]",
  cancelled: "bg-white/5 text-slate-500",
};

const CATEGORIES = ["Fashion", "Electronics", "Books", "Organic Products", "Toys", "Home Decor"];

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

const EMPTY_PRODUCT = { name: "", price: "", category: "Fashion", stock: "", description: "", emoji: "", photo_path: "", active: true, subcategory: "", size: "" };

const ProductsPanel = () => {
  const masters = useMasters();
  const [products, setProducts] = useState(null);
  const catData = masters.product_categories || {};
  const catOptions = Object.keys(catData);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products/mine`, headers());
      setProducts(data.products);
    } catch {
      setProducts([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setForm({ ...EMPTY_PRODUCT });
    setEditId(null);
    setPhotoPreview("");
  };

  const openEdit = (p) => {
    setForm({ name: p.name, price: String(p.price), category: p.category, stock: String(p.stock), description: p.description, emoji: p.emoji, photo_path: "", active: p.active, subcategory: p.subcategory || "", size: p.size || "" });
    setEditId(p.id);
    setPhotoPreview(p.photo_url ? `${BACKEND}${p.photo_url}` : "");
  };

  const pickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/products/photo`, fd, headers());
      setForm((f) => ({ ...f, photo_path: data.path }));
      setPhotoPreview(`${BACKEND}${data.url}`);
      toast.success("Photo uploaded 📸");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        price: parseInt(form.price, 10) || 0,
        category: form.category,
        stock: parseInt(form.stock, 10) || 0,
        description: form.description,
        emoji: form.emoji,
        active: form.active,
        subcategory: form.subcategory || "",
        size: form.size || "",
        ...(form.photo_path ? { photo_path: form.photo_path } : {}),
      };
      if (editId) {
        await axios.patch(`${API}/products/${editId}`, { ...EMPTY_PRODUCT, ...payload }, headers());
        toast.success("Product updated 🏪");
      } else {
        await axios.post(`${API}/products`, payload, headers());
        toast.success("Product listed on ShopBite 🎉");
      }
      setForm(null);
      setEditId(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    try {
      await axios.delete(`${API}/products/${p.id}`, headers());
      toast.success(`"${p.name}" removed`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.patch(
        `${API}/products/${p.id}`,
        { name: p.name, price: p.price, category: p.category, stock: p.stock, description: p.description, emoji: p.emoji, active: !p.active, subcategory: p.subcategory || "", size: p.size || "" },
        headers()
      );
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="products-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-400">Products you list here go live on ShopBite for every student.</p>
        <button
          data-testid="product-add"
          onClick={openAdd}
          className="flex shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white"
        >
          <Plus className="h-3.5 w-3.5" /> Add Product
        </button>
      </div>

      {form && (
        <div className="mt-6 rounded-3xl border border-[#7C3AED]/30 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="product-form">
          <p className="font-display text-lg font-bold">{editId ? "Edit product" : "New product"}</p>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <input
              data-testid="product-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Product name (e.g. Vintage Denim Jacket)"
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60"
            />
            <select
              data-testid="product-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="hidden"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div className="sm:col-span-2">
              <PillSelect
                label="Category"
                options={catOptions}
                value={form.category}
                onChange={(v) => setForm({ ...form, category: v, subcategory: "", size: "" })}
                testid="product-category-pills"
              />
            </div>
            {form.category && (catData[form.category]?.subs || []).length > 0 && (
              <div className="sm:col-span-2">
                <PillSelect
                  label="Subcategory"
                  options={catData[form.category].subs}
                  value={form.subcategory}
                  onChange={(v) => setForm({ ...form, subcategory: v })}
                  testid="product-subcategory"
                />
              </div>
            )}
            {form.subcategory && (catData[form.category]?.sizes || []).length > 0 && (
              <div className="sm:col-span-2">
                <PillSelect
                  label="Size"
                  options={catData[form.category].sizes}
                  value={form.size}
                  onChange={(v) => setForm({ ...form, size: v })}
                  testid="product-size"
                />
              </div>
            )}
            <div className="flex gap-3">
              <input
                data-testid="product-price"
                type="number"
                min="1"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="₹ Price"
                className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60"
              />
              <input
                data-testid="product-stock"
                type="number"
                min="0"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                placeholder="Stock"
                className="w-full rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60"
              />
              <input
                data-testid="product-emoji"
                value={form.emoji}
                onChange={(e) => setForm({ ...form, emoji: e.target.value })}
                placeholder="📦"
                className="w-20 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-center text-lg outline-none focus:border-[#7C3AED]/60"
              />
            </div>
            <input
              data-testid="product-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Short description (optional)"
              className="rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60"
            />
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <button
              data-testid="product-photo-btn"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-xs font-bold text-slate-200 transition-colors hover:border-[#7C3AED]/60 disabled:opacity-50"
            >
              {uploading ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}
              {uploading ? "Uploading…" : photoPreview ? "Change photo" : "Add photo"}
            </button>
            <input ref={fileRef} data-testid="product-photo" type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={pickPhoto} />
            {photoPreview && (
              <img src={photoPreview} alt="Product" data-testid="product-photo-preview" className="h-14 w-14 rounded-xl border border-white/10 object-cover" />
            )}
            <label className="flex items-center gap-2 text-xs text-slate-400">
              <input
                data-testid="product-active"
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                className="h-4 w-4 accent-[#06B6D4]"
              />
              Live on ShopBite
            </label>
          </div>
          <div className="mt-5 flex gap-2.5">
            <button
              data-testid="product-save"
              onClick={save}
              disabled={saving || !form.name.trim() || !form.price}
              className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-7 py-3 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
            >
              {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : editId ? "Save changes" : "List product"}
            </button>
            <button
              data-testid="product-cancel"
              onClick={() => { setForm(null); setEditId(null); }}
              className="rounded-full border border-white/15 px-6 py-3 text-xs font-bold text-slate-400 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="vendor-products">
        {products === null ? (
          <LoaderCircle className="h-6 w-6 animate-spin text-[#06B6D4]" />
        ) : products.length === 0 ? (
          <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-sm text-slate-500 sm:col-span-2">
            No products yet — list your first one 👆
          </p>
        ) : (
          products.map((p) => (
            <div key={p.id} data-testid={`product-${p.id.slice(-6)}`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              {p.photo_url ? (
                <img src={`${BACKEND}${p.photo_url}`} alt={p.name} className="h-14 w-14 rounded-xl border border-white/10 object-cover" />
              ) : (
                <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-white/5 text-3xl">{p.emoji}</span>
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold">{p.name}</p>
                <p className="font-jbmono text-[11px] text-[#22C55E]">
                  ₹{p.price} · {[p.category, p.subcategory, p.size].filter(Boolean).join(" › ")} · stock {p.stock}
                </p>
                <button
                  data-testid={`product-toggle-${p.id.slice(-6)}`}
                  onClick={() => toggleActive(p)}
                  className={`mt-1 rounded-full px-2.5 py-0.5 font-jbmono text-[9px] font-bold uppercase tracking-wider ${
                    p.active ? "bg-[#06B6D4]/10 text-[#06B6D4]" : "bg-white/5 text-slate-500"
                  }`}
                >
                  {p.active ? "● Live" : "○ Hidden"}
                </button>
              </div>
              <button
                data-testid={`product-edit-${p.id.slice(-6)}`}
                onClick={() => openEdit(p)}
                className="rounded-full border border-white/10 p-2 text-slate-400 hover:border-[#7C3AED]/60 hover:text-white"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid={`product-delete-${p.id.slice(-6)}`}
                onClick={() => remove(p)}
                className="rounded-full border border-white/10 p-2 text-slate-400 hover:border-[#F97316]/60 hover:text-[#F97316]"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default function VendorMode() {
  const { user, checked } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/shop/orders/incoming`, headers());
      setOrders(data.orders);
    } catch {
      setOrders([]);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [user, load]);

  const advance = async (orderId, status) => {
    setBusyId(orderId);
    try {
      await axios.patch(`${API}/shop/orders/${orderId}/status`, { status }, headers());
      toast.success(`Order #${orderId.slice(-6)} → ${status}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#09090B]">
        <LoaderCircle className="h-8 w-8 animate-spin text-[#06B6D4]" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const active = (orders || []).filter((o) => !["delivered", "cancelled"].includes(o.status));
  const revenue = (orders || []).filter((o) => o.status !== "cancelled").reduce((a, o) => a + o.total, 0);

  return (
    <div data-testid="vendor-mode" className="min-h-screen bg-[#09090B] text-white">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#09090B]/80 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3.5 sm:px-6">
          <Link to="/" data-testid="vendor-logo" className="flex items-center gap-2">
            <Logo className="h-8 w-8" />
            <span className="font-display text-base font-extrabold tracking-tight">
              FUN<span className="text-[#06B6D4]">Z</span>
              <span className="ml-2 rounded-full bg-[#7C3AED]/20 px-2.5 py-0.5 font-jbmono text-[9px] uppercase tracking-widest text-[#A78BFA]">
                Vendor Hub
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-2.5">
            <button
              data-testid="vendor-refresh"
              onClick={load}
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:border-[#06B6D4]/60 hover:text-white"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <Link
              to="/app"
              data-testid="vendor-back"
              className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-white"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> My FUNZ
            </Link>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <h1 className="flex items-center gap-3 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">
          <Store className="h-8 w-8 text-[#7C3AED]" /> Vendor Hub
        </h1>

        <div className="mt-6 flex gap-2" data-testid="vendor-tabs">
          {[
            { id: "orders", label: "🧾 Orders" },
            { id: "products", label: "📦 Products" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`vendor-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider transition-colors ${
                tab === t.id ? "bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] text-white" : "border border-white/10 text-slate-400 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "products" ? (
          <div className="mt-8">
            <ProductsPanel />
          </div>
        ) : (
          <>
            <p className="mt-2 text-sm text-slate-400">Incoming shop orders, live. Refreshes every 15 seconds.</p>

            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { label: "To fulfil", value: active.length },
                { label: "Total orders", value: (orders || []).length },
                { label: "Revenue", value: `₹${revenue}` },
              ].map((s, i) => (
                <div key={s.label} data-testid={`vendor-stat-${i}`} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5 backdrop-blur-xl">
                  <p className="font-display text-2xl font-extrabold">{s.value}</p>
                  <p className="mt-1 font-jbmono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 space-y-4" data-testid="vendor-orders">
              {orders === null ? (
                <LoaderCircle className="h-6 w-6 animate-spin text-[#06B6D4]" />
              ) : orders.length === 0 ? (
                <p className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center text-sm text-slate-500">
                  No orders yet — your ShopBite listings are live 🛍️
                </p>
              ) : (
                orders.map((o) => (
                  <div
                    key={o.id}
                    data-testid={`vendor-order-${o.id.slice(-6)}`}
                    className={`rounded-3xl border p-5 backdrop-blur-xl transition-colors sm:p-6 ${
                      o.status === "placed" ? "border-[#22C55E]/30 bg-[#22C55E]/[0.04]" : "border-white/10 bg-white/[0.03]"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-jbmono text-sm font-bold text-[#06B6D4]">#{o.id.slice(-6)}</span>
                      <span className={`rounded-full px-3 py-1 font-jbmono text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[o.status]}`}>
                        {o.status}
                      </span>
                      <span className="ml-auto font-jbmono text-[11px] text-slate-500">
                        {String(o.created_at).slice(0, 16).replace("T", " ")}
                      </span>
                    </div>
                    <p className="mt-3 text-sm font-semibold text-white">
                      {o.items.map((i) => `${i.qty}× ${i.name}`).join(" · ")}
                      <span className="ml-2 font-jbmono text-[#22C55E]">₹{o.total}</span>
                    </p>
                    <p className="mt-1.5 font-jbmono text-[11px] text-slate-500">
                      {o.email} · {o.phone} · {o.address}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {NEXT[o.status] && (
                        <button
                          data-testid={`vendor-advance-${o.id.slice(-6)}`}
                          onClick={() => advance(o.id, NEXT[o.status].to)}
                          disabled={busyId === o.id}
                          className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50"
                        >
                          {busyId === o.id ? "…" : NEXT[o.status].label}
                        </button>
                      )}
                      {!["delivered", "cancelled"].includes(o.status) && (
                        <button
                          data-testid={`vendor-cancel-${o.id.slice(-6)}`}
                          onClick={() => advance(o.id, "cancelled")}
                          disabled={busyId === o.id}
                          className="rounded-full border border-white/15 px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-slate-400 transition-colors hover:border-[#F97316]/60 hover:text-[#F97316]"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
